import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Button57 from '../components/Button57';
import { storage } from "../common/storage";

const { FiHeart, FiTrash2, FiInfo, FiX } = FiIcons;

const Bookmarks = () => {
  const [bookmarkedItems, setBookmarkedItems] = useState([]);
  const [wasClearedNotice, setWasClearedNotice] = useState(false);

  useEffect(() => {
    const items = storage.getBookmarks();
    setBookmarkedItems(items);

    // Check if empty but visited before to show generic "cleared" notice
    const visitedBefore = storage.hasVisitedBefore();
    if (items.length === 0 && visitedBefore) {
      setWasClearedNotice(true);
    }

    const updateFromStorage = () => {
      setBookmarkedItems(storage.getBookmarks());
    };

    window.addEventListener('storage_update', updateFromStorage);
    window.addEventListener('storage', updateFromStorage);
    
    return () => {
      window.removeEventListener('storage_update', updateFromStorage);
      window.removeEventListener('storage', updateFromStorage);
    };
  }, []);

  const removeBookmark = (itemName) => {
    const updated = bookmarkedItems.filter(item => item.name !== itemName);
    setBookmarkedItems(updated);
    storage.saveBookmarks(updated);
  };

  const clearAll = () => {
    setBookmarkedItems([]);
    storage.clearBookmarks();
  };

  const googleMapsUrl = "https://www.google.com/maps/place/Ganda+Sushi/@40.726154,-73.789221,17z/data=!3m1!4b1!4m6!3m5!1s0x89c26100ca7163ad:0x4b7fe7e097bf9f58!8m2!3d40.726154!4d-73.789221!16s%2Fg%2F1thsb8h7?entry=ttu&g_ep=EgoyMDI2MDEwNC4wIKXMDSoKLDEwMDc5MjA3MUgBUAM%3D";

  return (
    <>
      <Helmet>
        <title>Your Bookmarks - Ganda Sushi</title>
        <meta name="description" content="View your curated list of favorite dishes from Ganda Sushi." />
      </Helmet>

      <main className="pt-32 pb-24 bg-luxury-cream min-h-screen text-luxury-dark selection:bg-luxury-gold selection:text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-8xl font-serif text-luxury-dark tracking-tight mb-4">Your Favorites</h1>
            <span className="text-luxury-gold tracking-[0.5em] uppercase text-[10px] font-black block">Curated Culinary Selections</span>
          </div>

          {/* Generic "Was Cleared" Notice */}
          <AnimatePresence>
            {wasClearedNotice && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="max-w-2xl mx-auto mb-10 p-6 bg-white border border-luxury-gold/20 rounded-2xl shadow-sm flex items-start gap-4 relative"
              >
                <div className="bg-luxury-gold/10 p-2.5 rounded-full">
                  <SafeIcon icon={FiInfo} className="text-luxury-gold w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-luxury-dark mb-1 tracking-tight">Items Missing?</h4>
                  <p className="text-xs text-luxury-dark/70 leading-relaxed font-light">
                    Your saved dishes may have been cleared by your device. Feel free to save them again.
                  </p>
                </div>
                <button 
                  onClick={() => setWasClearedNotice(false)}
                  className="text-luxury-dark/30 hover:text-luxury-dark transition-colors p-1"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {bookmarkedItems.length > 0 ? (
            <div className="space-y-10">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                {/* Enhanced Device-Generic Helper Line */}
                <div className="flex items-center gap-3 py-3 px-5 bg-white/50 backdrop-blur-sm border border-luxury-gold/10 rounded-full shadow-sm max-w-2xl">
                  <div className="flex-shrink-0 w-6 h-6 bg-luxury-gold/10 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiInfo} className="text-luxury-gold w-3.5 h-3.5" />
                  </div>
                  <p className="text-[11px] md:text-xs text-luxury-dark/70 leading-snug font-medium tracking-wide">
                    <span className="text-luxury-gold font-bold">Note:</span> Your saved dishes are stored on this device only. They may be cleared by your browser or device over time — you can re-save your favorites anytime.
                  </p>
                </div>

                <button 
                  onClick={clearAll}
                  className="text-luxury-dark/70 hover:text-red-500 transition-colors flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-black ml-auto md:ml-0"
                >
                  <SafeIcon icon={FiTrash2} /> Clear All
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence>
                  {bookmarkedItems.map((item) => (
                    <motion.div
                      key={item.name}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="bg-white p-8 border border-luxury-gold/10 relative group rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                    >
                      <button 
                        onClick={() => removeBookmark(item.name)}
                        className="absolute top-6 right-6 text-luxury-gold hover:scale-110 transition-transform"
                        aria-label="Remove from favorites"
                      >
                        <SafeIcon icon={FiHeart} className="fill-current w-5 h-5" />
                      </button>
                      <div className="mb-4">
                        <span className="text-[10px] tracking-widest uppercase font-black text-luxury-gold/80 block mb-2">{item.category}</span>
                        <h3 className="text-2xl font-serif text-luxury-dark mb-2">{item.name}</h3>
                        <p className="text-luxury-gold font-serif text-xl">${item.price.toFixed(2)}</p>
                      </div>
                      {item.desc && (
                        <p className="text-luxury-dark/70 text-sm italic font-light leading-relaxed">
                          {item.desc}
                        </p>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          ) : (
            <div className="text-center py-24 border border-dashed border-luxury-gold/20 rounded-2xl bg-white/30">
              <SafeIcon icon={FiHeart} className="w-12 h-12 text-luxury-gold/20 mx-auto mb-6" />
              <p className="text-luxury-dark/70 font-light text-xl mb-8">Your curated list is currently empty.</p>
              <Button57 to="/menu" text="Explore Menu" altText="Go to Menu" className="mx-auto" />
            </div>
          )}

          {/* Action Section */}
          <div className="mt-40 p-20 bg-white border border-luxury-gold/10 text-center rounded-2xl shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-luxury-gold" />
            <h2 className="text-5xl font-serif text-luxury-dark mb-8">Ready to Taste?</h2>
            <p className="text-luxury-dark/80 mb-12 max-w-xl mx-auto text-lg leading-relaxed font-light">
              Your favorites are waiting. Call us to place your order or visit us for an unforgettable dining experience.
            </p>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6">
              <Button57 href="tel:+17183801818" text="Order Now" altText="Call to Order" className="min-w-[200px]" />
              <Button57 href={googleMapsUrl} text="Directions" altText="Open Maps" className="min-w-[200px]" />
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Bookmarks;